import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ShieldOff } from "lucide-react";

export function ResetCustomerPinButton({ customerId, customerName }: { customerId: string; customerName: string }) {
  const { user } = useAuth();
  const { toast } = useToast();

  if (user?.role !== "SUPER") return null;

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/admin/customers/${customerId}/reset-pin`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to reset PIN");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "PIN reset",
        description: `${customerName} will be prompted to set a new PIN on next login.`,
      });
    },
    onError: (err: Error) => {
      toast({ title: "Failed", description: err.message, variant: "destructive" });
    },
  });

  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-2"
      onClick={() => {
        if (confirm(`Reset PIN for ${customerName}? They will be required to set a new one on next login.`)) {
          mutation.mutate();
        }
      }}
      disabled={mutation.isPending}
    >
      <ShieldOff className="h-3.5 w-3.5" />
      {mutation.isPending ? "Resetting..." : "Reset PIN"}
    </Button>
  );
}
